import { useMemo } from "react";
import { usePermissions } from "@/context/PermissionsProvider";
import {
  findBranch,
  findRootLegalEntityForSelection,
} from "@/modules/subscription/billingAccess";
import type { LegalEntityHierarchy } from "@/services/profileService";
import { useProfileStore } from "@/store/profileStore";
import { useSettingsStore } from "@/store/settingsStore";

/**
 * Comma-separated legal-entity (account) ids that may see Campaigns, Labels,
 * and the Contacts "Manage Labels" action. Empty means nobody — this is an
 * opt-in allowlist. Use the organisation root id (the top dropdown), not a
 * child legal entity or branch.
 */
export function getCampaignsLabelsEnabledAccountIds(): string[] {
  return (import.meta.env.VITE_CAMPAIGNS_LABELS_ENABLED_ACCOUNT_IDS ?? "")
    .split(",")
    .map((item) => normalizeId(item))
    .filter(Boolean);
}

function normalizeId(id: string | null | undefined): string {
  return id?.trim().toLowerCase() ?? "";
}

function addId(into: Set<string>, id: string | null | undefined) {
  const normalized = normalizeId(id);
  if (normalized) into.add(normalized);
}

function addIds(into: Set<string>, ids: string[] | null | undefined) {
  for (const id of ids ?? []) addId(into, id);
}

/** Path from a root legal entity down to `targetId`, inclusive. */
function legalEntityPathIds(
  entities: LegalEntityHierarchy[],
  targetId: string | null,
): string[] {
  if (!targetId) return [];

  const walk = (nodes: LegalEntityHierarchy[], ancestors: string[]): string[] | null => {
    for (const node of nodes) {
      const path = [...ancestors, node.id];
      if (node.id === targetId) return path;
      const found = walk(node.subEntities ?? [], path);
      if (found) return found;
    }
    return null;
  };

  return walk(entities, []) ?? [targetId];
}

export function isCampaignsLabelsEnabledForAccount(params: {
  legalEntityId: string | null;
  branchId: string | null;
  legalEntities: LegalEntityHierarchy[];
  unrestricted?: boolean;
  accessibleRootLegalEntityIds?: string[] | null;
  visibleLegalEntityIds?: string[] | null;
  meLegalEntityId?: string | null;
  selectedOrganisationId?: string | null;
}): boolean {
  const allowed = new Set(getCampaignsLabelsEnabledAccountIds());
  if (allowed.size === 0) return false;

  const {
    legalEntityId,
    branchId,
    legalEntities,
    unrestricted,
    accessibleRootLegalEntityIds,
    visibleLegalEntityIds,
    meLegalEntityId,
    selectedOrganisationId,
  } = params;

  const candidates = new Set<string>();
  addId(candidates, legalEntityId);
  addId(candidates, meLegalEntityId);
  addId(candidates, selectedOrganisationId);
  addIds(candidates, legalEntityPathIds(legalEntities, legalEntityId));

  const orgRoot = findRootLegalEntityForSelection(legalEntities, legalEntityId, branchId);
  addId(candidates, orgRoot?.id);

  const selectedBranch = findBranch(legalEntities, branchId);
  addId(candidates, selectedBranch?.legalEntityId);

  // Restricted users (branch managers, etc.) have no org dropdown. The header
  // legal entity is often a child office, while the allowlist uses the account
  // root from the master dropdown. /me carries the real organisation roots.
  if (!unrestricted) {
    addIds(candidates, accessibleRootLegalEntityIds);
    addIds(candidates, visibleLegalEntityIds);
  }

  for (const id of candidates) {
    if (allowed.has(id)) return true;
  }
  return false;
}

const EMPTY_LEGAL_ENTITIES: LegalEntityHierarchy[] = [];

export function useCampaignsLabelsEnabled(): boolean {
  const legalEntityId = useSettingsStore((s) => s.legalEntityId);
  const branchId = useSettingsStore((s) => s.branchId);
  const legalEntities = useProfileStore((s) => s.profileData?.legalEntities);
  const { me } = usePermissions();

  return useMemo(
    () =>
      isCampaignsLabelsEnabledForAccount({
        legalEntityId,
        branchId,
        legalEntities: legalEntities ?? EMPTY_LEGAL_ENTITIES,
        unrestricted: me?.unrestricted === true,
        accessibleRootLegalEntityIds: me?.accessibleRootLegalEntityIds,
        visibleLegalEntityIds: me?.visibleLegalEntityIds,
        meLegalEntityId: me?.legalEntityId,
        selectedOrganisationId: me?.selectedOrganisationId,
      }),
    [legalEntityId, branchId, legalEntities, me],
  );
}
